import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Plus } from 'lucide-react';

import type { DashboardTodayItem } from '../types';
import { activityTypeBarStyle } from '../../../utils/activityTypeColors';

const COLLAPSED_COUNT = 5;

type DashboardTodayPanelProps = {
  items: DashboardTodayItem[];
  onCreateActivity?: () => void;
};

export function DashboardTodayPanel({ items, onCreateActivity }: DashboardTodayPanelProps) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [expanded, setExpanded] = useState(false);

  const canExpand = items.length > COLLAPSED_COUNT;
  const visible = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const hiddenCount = items.length - COLLAPSED_COUNT;

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-app-border bg-app-surface p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-app-text">Heute</h2>
        {onCreateActivity ? (
          <button
            type="button"
            onClick={onCreateActivity}
            className="inline-flex items-center gap-1 rounded-lg border border-app-border px-2.5 py-1.5 text-xs font-semibold text-app-text-muted transition-colors hover:bg-app-hover"
          >
            <Plus className="h-3.5 w-3.5" />
            Aktivität
          </button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <p className="py-6 text-center text-xs text-slate-400">Keine Termine für heute.</p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {visible.map((item) => {
              const typeStyle = activityTypeBarStyle(item.activityType);
              const content = (
                <>
                  <span className="w-11 shrink-0 pt-0.5 text-xs font-semibold text-app-text-subtle tabular-nums">{item.time}</span>
                  <span className="relative mt-0.5 flex h-7 w-1.5 shrink-0 items-start justify-center">
                    <span
                      className="h-full w-1 rounded-full"
                      style={{ backgroundColor: typeStyle.accent }}
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-xs font-semibold text-app-text">{item.title}</span>
                      {item.done ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> : null}
                    </span>
                    {item.subtitle ? (
                      <span className="block truncate text-[11px] text-app-text-subtle" title={item.subtitle}>
                        {item.subtitle}
                      </span>
                    ) : null}
                  </span>
                </>
              );

              return (
                <li key={item.id}>
                  {item.leadId ? (
                    <Link
                      to={`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/${encodeURIComponent(item.leadId)}`}
                      className="flex min-w-0 items-start gap-3 rounded-lg px-2 py-1 transition-colors hover:bg-app-hover"
                    >
                      {content}
                    </Link>
                  ) : (
                    <div className="flex min-w-0 items-start gap-3 px-2 py-1">{content}</div>
                  )}
                </li>
              );
            })}
          </ul>
          {canExpand ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg py-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50"
            >
              {expanded ? (
                <>
                  Weniger anzeigen
                  <ChevronUp className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  {hiddenCount} weitere anzeigen
                  <ChevronDown className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
